import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 泛知识与生活百科 - 横屏「对比分析」风格
 *
 * 经典 A vs B 对比版式，居中分割线，左右双栏对比，适用于产品/方法/习惯等对比
 * 适合：A 和 B 哪个好、正确 vs 错误做法、不同方案对比
 * 尺寸：1920x1080 横屏
 */

interface CompareRow {
  dimension: string;
  left: string;
  right: string;
  winner?: 'left' | 'right' | 'tie';
}

interface LifeComparisonProps {
  topic: string;
  leftLabel: string;
  leftIcon: string;
  leftColor: string;
  rightLabel: string;
  rightIcon: string;
  rightColor: string;
  rows: CompareRow[];
  verdict: string;
  verdictDetail: string;
}

const DEFAULT_PROPS: LifeComparisonProps = {
  topic: '早起 vs 晚睡：哪个更高效？',
  leftLabel: '早起型',
  leftIcon: '🌅',
  leftColor: '#f59e0b',
  rightLabel: '晚睡型',
  rightIcon: '🌙',
  rightColor: '#6366f1',
  rows: [
    { dimension: '专注力峰值', left: '上午 9-11 点', right: '晚上 9-12 点', winner: 'tie' },
    { dimension: '身体激素', left: '皮质醇自然升高，精力充沛', right: '褪黑素延迟，需要额外刺激', winner: 'left' },
    { dimension: '社交适配', left: '与社会节奏一致', right: '容易错过上午安排', winner: 'left' },
    { dimension: '创造力', left: '适合逻辑分析类工作', right: '适合发散性创意工作', winner: 'tie' },
    { dimension: '长期健康', left: '与昼夜节律一致，睡眠质量更高', right: '易导致慢性睡眠不足', winner: 'left' },
  ],
  verdict: '早起型整体略优',
  verdictDetail: '但最重要的是：保持固定的作息节奏，比早晚本身更关键',
};

export const LifeComparisonTemplate: React.FC<Partial<LifeComparisonProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Header (0-40)
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  // Divider line
  const dividerHeight = interpolate(frame, [20, 60], [0, 600], { extrapolateRight: 'clamp' });

  // Labels (30-60)
  const labelProgress = spring({ frame: frame - 30, fps, config: { damping: 14, stiffness: 120 } });

  // Rows
  const rowStart = 70;
  const rowInterval = 50;

  // Verdict
  const verdictStart = rowStart + p.rows.length * rowInterval + 20;
  const verdictOpacity = interpolate(frame, [verdictStart, verdictStart + 25], [0, 1], { extrapolateRight: 'clamp' });
  const verdictScale = spring({
    frame: frame - verdictStart,
    fps,
    from: 0.9,
    to: 1,
    config: { damping: 14, stiffness: 100 },
  });

  return (
    <AbsoluteFill
      style={{
        background: '#0f172a',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Left/Right background tints */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '50%',
          bottom: 0,
          background: `linear-gradient(180deg, ${p.leftColor}08, transparent 60%)`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '50%',
          bottom: 0,
          background: `linear-gradient(180deg, ${p.rightColor}08, transparent 60%)`,
        }}
      />

      {/* Topic */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: headerProgress,
          transform: `translateY(${(1 - headerProgress) * -20}px)`,
        }}
      >
        <h1 style={{ fontSize: 46, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>{p.topic}</h1>
      </div>

      {/* Center divider */}
      <div
        style={{
          position: 'absolute',
          top: 140,
          left: '50%',
          width: 2,
          height: dividerHeight,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.3), rgba(255,255,255,0.05))',
          transform: 'translateX(-50%)',
        }}
      />

      {/* VS badge */}
      <div
        style={{
          position: 'absolute',
          top: 140,
          left: '50%',
          transform: `translate(-50%, 0) scale(${labelProgress})`,
          zIndex: 2,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#1e293b',
            border: '2px solid rgba(255,255,255,0.2)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 22,
            fontWeight: 900,
            color: 'rgba(255,255,255,0.6)',
          }}
        >
          VS
        </div>
      </div>

      {/* Left label */}
      <div
        style={{
          position: 'absolute',
          top: 140,
          left: 80,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          opacity: labelProgress,
          transform: `translateX(${(1 - labelProgress) * -30}px)`,
        }}
      >
        <span style={{ fontSize: 40 }}>{p.leftIcon}</span>
        <span style={{ fontSize: 32, fontWeight: 800, color: p.leftColor }}>{p.leftLabel}</span>
      </div>

      {/* Right label */}
      <div
        style={{
          position: 'absolute',
          top: 140,
          right: 80,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          opacity: labelProgress,
          transform: `translateX(${(1 - labelProgress) * 30}px)`,
        }}
      >
        <span style={{ fontSize: 36, fontWeight: 800, color: p.rightColor }}>{p.rightLabel}</span>
        <span style={{ fontSize: 40 }}>{p.rightIcon}</span>
      </div>

      {/* Comparison rows */}
      <div
        style={{
          position: 'absolute',
          top: 240,
          left: 48,
          right: 48,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {p.rows.map((row, i) => {
          const start = rowStart + i * rowInterval;
          const rowProgress = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 16, stiffness: 120, mass: 0.5 },
          });

          const leftWin = row.winner === 'left';
          const rightWin = row.winner === 'right';

          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 0,
                opacity: rowProgress,
                transform: `translateY(${(1 - rowProgress) * 20}px)`,
              }}
            >
              {/* Left cell */}
              <div
                style={{
                  flex: 1,
                  padding: '18px 24px',
                  background: leftWin ? `${p.leftColor}12` : 'rgba(255,255,255,0.03)',
                  borderRadius: '14px 0 0 14px',
                  border: leftWin ? `1px solid ${p.leftColor}30` : '1px solid rgba(255,255,255,0.05)',
                  borderRight: 'none',
                  textAlign: 'right',
                }}
              >
                <div style={{ fontSize: 26, color: leftWin ? p.leftColor : 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                  {leftWin && '✓ '}{row.left}
                </div>
              </div>

              {/* Dimension label (center) */}
              <div
                style={{
                  width: 160,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'rgba(255,255,255,0.06)',
                  borderTop: '1px solid rgba(255,255,255,0.05)',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 22, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                  {row.dimension}
                </span>
              </div>

              {/* Right cell */}
              <div
                style={{
                  flex: 1,
                  padding: '18px 24px',
                  background: rightWin ? `${p.rightColor}12` : 'rgba(255,255,255,0.03)',
                  borderRadius: '0 14px 14px 0',
                  border: rightWin ? `1px solid ${p.rightColor}30` : '1px solid rgba(255,255,255,0.05)',
                  borderLeft: 'none',
                }}
              >
                <div style={{ fontSize: 26, color: rightWin ? p.rightColor : 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                  {rightWin && '✓ '}{row.right}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Verdict */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: verdictOpacity,
          transform: `scale(${verdictScale})`,
        }}
      >
        <div style={{ fontSize: 32, fontWeight: 800, color: '#fbbf24', marginBottom: 12 }}>
          📌 {p.verdict}
        </div>
        <div style={{ fontSize: 28, color: 'rgba(255,255,255,0.5)', maxWidth: 1300, margin: '0 auto' }}>
          {p.verdictDetail}
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, verdictStart + 60], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: `linear-gradient(90deg, ${p.leftColor}, ${p.rightColor})`,
        }}
      />
    </AbsoluteFill>
  );
};
